import { useEffect } from 'react';

interface MetaTagConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

// Helper function to get preview image URL (PNG preferred, SVG fallback)
const getPreviewImageUrl = (page: string): string => {
  const pageMap: Record<string, string> = {
    'Take-Home Pay': 'takehome-pay',
    'Income Tax': 'income-tax', 
    'Retirement Income': 'retirement-income',
    'After-Tax Retirement Income': 'after-tax-retirement'
  };
  
  const pageKey = pageMap[page] || 'takehome-pay';
  
  // Try PNG first, fallback to SVG if available
  // In a real application, you might check if the PNG exists
  // For now, we'll prefer PNG as that's the goal
  return `/previews/preview-${pageKey}.png`;
};

// Dynamic meta configurations for each page
const createPageMetaConfig = (page: string): MetaTagConfig => {
  const configs: Record<string, Omit<MetaTagConfig, 'image'>> = {
    'Take-Home Pay': {
      title: 'Take-Home Pay Calculator | MoneyMath',
      description: 'Calculate your actual take-home pay after taxes, deductions, and charitable giving. See exactly how much you keep from your paycheck.',
      url: 'https://moneymath.fyi#takehome-pay'
    },
    'Income Tax': {
      title: 'Income Tax Calculator | MoneyMath',
      description: 'Calculate federal, state, Social Security, and Medicare taxes. Get a detailed breakdown of your tax obligations.',
      url: 'https://moneymath.fyi#income-tax'
    },
    'Retirement Income': {
      title: 'Retirement Income Calculator | MoneyMath',
      description: 'Plan your retirement with detailed projections for 401k, IRA, and investment accounts. See your future financial security.',
      url: 'https://moneymath.fyi#retirement-income'
    },
    'After-Tax Retirement Income': {
      title: 'After-Tax Retirement Optimizer | MoneyMath',
      description: 'Optimize your retirement strategy for maximum after-tax income. Compare traditional vs Roth contributions.',
      url: 'https://moneymath.fyi#after-tax-retirement'
    }
  };

  const baseConfig = configs[page] || configs['Take-Home Pay'];
  return {
    ...baseConfig,
    image: getPreviewImageUrl(page)
  };
};

/**
 * Updates document meta tags for better SEO and social sharing
 */
export const updateMetaTags = (config: MetaTagConfig) => {
  if (config.title) {
    document.title = config.title;
    updateMetaProperty('og:title', config.title);
    updateMetaProperty('twitter:title', config.title);
  }

  if (config.description) {
    updateMetaTag('description', config.description);
    updateMetaProperty('og:description', config.description);
    updateMetaProperty('twitter:description', config.description);
  }

  if (config.image) {
    updateMetaProperty('og:image', config.image);
    updateMetaProperty('twitter:image', config.image);
  }

  if (config.url) {
    updateMetaProperty('og:url', config.url);
    updateLinkTag('canonical', config.url);
  }
};

/**
 * Helper function to update meta tags by name
 */
const updateMetaTag = (name: string, content: string) => {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

/**
 * Helper function to update meta tags by property
 */
const updateMetaProperty = (property: string, content: string) => {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
};

/**
 * Helper function to update link tags
 */
const updateLinkTag = (rel: string, href: string) => {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
};

/**
 * React hook to automatically update meta tags when page changes
 */
export const usePageMetaTags = (selectedPage: string) => {
  useEffect(() => {
    const config = createPageMetaConfig(selectedPage);
    updateMetaTags(config);
  }, [selectedPage]);
};

/**
 * Function to get meta config for a specific page with dynamic preview
 */
export const getPageMetaConfig = (page: string): MetaTagConfig => {
  return createPageMetaConfig(page);
};

export default getPageMetaConfig;